// Category lens: "Preview as category…" cascade picker + the read-only
// preview panel it drives. Deliberately reads the REAL dictionary endpoint
// (useGetAttributeDictionaryApi with catalogCategoryId) rather than
// re-deriving anything client-side from the admin types list — zero drift
// from what an editor actually sees for that category (value-level gating
// included, since the BE already filters it there). See
// product-attributes-screen.tsx for how this replaces the management view.

import { useGetAttributeDictionaryApi } from 'apis/product-attributes';
import { CategoryModal } from 'components/category-picker/category-modal';
import type { CategoryRef } from 'components/category-picker/types';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButtonWithTooltip,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from 'design-system';
import { FiEye, FiX } from 'react-icons/fi';

interface ToggleProps {
  value: CategoryRef | null;
  onChange: (category: CategoryRef | null) => void;
  isDisabled?: boolean;
}

export const CategoryLensToggle = ({ value, onChange, isDisabled }: ToggleProps) => {
  if (value) {
    return (
      <HStack spacing='2'>
        <Badge colorScheme='purple' variant='subtle' px='2' py='1' fontSize='xs'>
          Previewing: {value.title}
        </Badge>
        <IconButtonWithTooltip
          size='xs'
          label='Clear preview'
          aria-label='clear-category-preview-btn'
          Icon={<Icon as={FiX} />}
          variant='unstyled'
          onClick={() => onChange(null)}
        />
      </HStack>
    );
  }

  return (
    <CategoryModal
      value={null}
      onSelect={(category) => onChange(category)}
      trigger={
        <Button size='sm' variant='outline' leftIcon={<Icon as={FiEye} />} isDisabled={isDisabled}>
          Preview as category…
        </Button>
      }
    />
  );
};

interface PreviewProps {
  category: CategoryRef;
}

export const CategoryLensPreview = ({ category }: PreviewProps) => {
  const { data, isLoading } = useGetAttributeDictionaryApi({ catalogCategoryId: category.id });
  const types = data?.data ?? [];

  return (
    <Box>
      <Alert status='info' mb='4' borderRadius='md'>
        <AlertIcon />
        <Text fontSize='sm'>
          Read-only preview — the attribute types &amp; values an editor would see for <b>{category.title}</b>, straight
          from the live dictionary endpoint (value-level category gating included).
        </Text>
      </Alert>

      {isLoading ? (
        <Flex justify='center' py='8'>
          <Spinner size='md' color='gray.400' />
        </Flex>
      ) : types.length === 0 ? (
        <Text color='gray.500' fontSize='sm' textAlign='center' py='8'>
          No attribute types apply to this category.
        </Text>
      ) : (
        <VStack align='stretch' spacing='3'>
          {types.map((type) => (
            <Box key={type.id} borderWidth='1px' borderColor='gray.200' borderRadius='md' p='3'>
              <Flex align='center' justify='space-between' mb='2'>
                <Text fontWeight='semibold' fontSize='sm'>
                  {type.label}
                </Text>
                <Badge fontSize='2xs' colorScheme='gray'>
                  {type.values.length} value{type.values.length === 1 ? '' : 's'}
                </Badge>
              </Flex>
              <Wrap spacing='1'>
                {type.values.map((value) => (
                  <WrapItem key={value.id}>
                    <Badge colorScheme='gray' variant='subtle'>
                      {value.label}
                    </Badge>
                  </WrapItem>
                ))}
                {type.values.length === 0 && (
                  <Text fontSize='xs' color='gray.400'>
                    No values available under this category.
                  </Text>
                )}
              </Wrap>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};
